/**
 * BarcodesTab — Manage scannable barcodes for this item — Tab 4 of the Item form.
 * Own state (NOT react-hook-form) — barcodes are a child resource.
 *
 * Props:
 *   itemId  string  — item UUID; null means item not yet saved
 *   uoms    array   — [{ value: uomId, label: 'Box (BOX)' }] from parent
 *
 * API:
 *   TODO: GET   /inventory/items/{itemId}/barcodes/         (getItemBarcodes)
 *   TODO: POST  /inventory/items/{itemId}/barcodes/         (addItemBarcode)
 *   TODO: PATCH /inventory/barcodes/{barcodeId}/            (setPrimaryBarcode)
 *   TODO: DELETE /inventory/barcodes/{barcodeId}/           (deleteItemBarcode)
 *   Mock: local state used until backend endpoints exist
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Typography, IconButton, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { DataGrid } from '@mui/x-data-grid';
import SectionCard from '../SectionCard';
import { InvTextField, InvSelect } from '../InvField';
import { BARCODE_TYPES } from '../../constants/itemClassification';
import { IPD_COLORS, tableContainerSx, cardSx } from '../../theme';
import { getItemBarcodes, addItemBarcode, setPrimaryBarcode, deleteItemBarcode } from '../../api/itemBarcode';

const EMPTY_NEW = { value: '', type: 'EAN13', uomId: '' };

// Hardcoded UOM pack options for barcodes.
// TODO: replace with uoms prop from parent once backend supports barcode-level UOM
const PACK_OPTIONS = [
  { value: 'box',   label: 'Box' },
  { value: 'strip', label: 'Strip' },
  { value: 'piece', label: 'Piece' },
];

export default function BarcodesTab({ itemId, uoms }) {
  const [barcodes, setBarcodes]             = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [addDialogOpen, setAddDialogOpen]   = useState(false);
  const [deleteTarget, setDeleteTarget]     = useState(null);
  const [deleteLoading, setDeleteLoading]   = useState(false);
  const [saveLoading, setSaveLoading]       = useState(false);
  const [formError, setFormError]           = useState('');
  const [newBarcode, setNewBarcode]         = useState(EMPTY_NEW);

  // ── Load on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!itemId) return;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // TODO: replace mock with: const data = await getItemBarcodes(itemId);
        setBarcodes([
          { id: '1', value: '8901030869488', type: 'EAN13', uomName: 'BOX', isPrimary: true },
        ]);
      } catch (err) {
        setError(err.message || 'Failed to load barcodes');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [itemId]);

  // ── Set primary ──────────────────────────────────────────────
  async function handleSetPrimary(barcodeId) {
    // Optimistic update
    setBarcodes(prev => prev.map(b => ({ ...b, isPrimary: b.id === barcodeId })));
    try {
      // TODO: await setPrimaryBarcode(barcodeId, { is_primary: true });
    } catch {
      // Revert on failure — reload
      setError('Failed to update primary barcode');
    }
  }

  // ── Add barcode ──────────────────────────────────────────────
  function openAdd() {
    setNewBarcode(EMPTY_NEW);
    setFormError('');
    setAddDialogOpen(true);
  }

  async function handleAdd() {
    const { value, type, uomId } = newBarcode;
    if (!value || value.trim().length < 4) return setFormError('Barcode must be at least 4 characters');
    if (!type)                             return setFormError('Select a barcode type');

    setSaveLoading(true);
    setFormError('');
    try {
      // TODO: replace with: await addItemBarcode(itemId, { value: value.trim(), type, uom_id: uomId });
      // Mock: push to local state
      const fakeId = `mock-${Date.now()}`;
      const uomName = PACK_OPTIONS.find(p => p.value === uomId)?.label || uomId || '';
      setBarcodes(prev => [
        ...prev,
        { id: fakeId, value: value.trim(), type, uomName, isPrimary: prev.length === 0 },
      ]);
      setAddDialogOpen(false);
    } catch (err) {
      setFormError(err.message || 'Failed to add barcode');
    } finally {
      setSaveLoading(false);
    }
  }

  // ── Delete barcode ───────────────────────────────────────────
  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      // TODO: await deleteItemBarcode(deleteTarget.id);
      setBarcodes(prev => prev.filter(b => b.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      // keep dialog open
    } finally {
      setDeleteLoading(false);
    }
  }

  // ── DataGrid columns ─────────────────────────────────────────
  const columns = [
    {
      field: 'value', headerName: 'Barcode', flex: 1, minWidth: 150,
      renderCell: p => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontFamily: 'monospace' }}>{p.value}</Typography>
          {p.row.isPrimary && (
            <Chip label="Primary" size="small"
              sx={{ height: 18, fontSize: 10, backgroundColor: `${IPD_COLORS.primary}22`,
                color: IPD_COLORS.primary, border: `1px solid ${IPD_COLORS.primary}44`,
                '& .MuiChip-label': { px: 0.75 } }} />
          )}
        </Box>
      ),
    },
    { field: 'type',    headerName: 'Type',    width: 110,
      renderCell: p => <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>{p.value}</Typography> },
    { field: 'uomName', headerName: 'Pack',    width: 90 },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: p => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={p.row.isPrimary ? 'Primary barcode' : 'Set as primary'}>
            <span>
              <IconButton
                size="small"
                disabled={p.row.isPrimary}
                onClick={() => handleSetPrimary(p.row.id)}
                sx={{ color: p.row.isPrimary ? IPD_COLORS.warning.text : IPD_COLORS.textSecondary }}
              >
                {p.row.isPrimary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteTarget(p.row)}
              sx={{ color: IPD_COLORS.error.text }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── Guard: item not saved yet ────────────────────────────────
  if (!itemId) {
    return <Alert severity="info">Save the item first to manage barcodes.</Alert>;
  }

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <SectionCard
        title="Barcodes"
        actions={
          <Button size="small" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ color: IPD_COLORS.primary, textTransform: 'none', fontSize: 13 }}>
            Add Barcode
          </Button>
        }
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && barcodes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <QrCodeIcon sx={{ fontSize: 48, color: IPD_COLORS.textSecondary, mb: 1.5 }} />
            <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 0.5 }}>No barcodes added yet.</Typography>
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>
              Add a barcode to enable scanning in GRN and dispense.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ ...tableContainerSx, ...cardSx, border: 'none', boxShadow: 'none' }}>
            <DataGrid
              rows={barcodes}
              columns={columns}
              loading={loading}
              autoHeight
              getRowId={r => r.id}
              hideFooter={barcodes.length <= 10}
              disableRowSelectionOnClick
              sx={{ border: 'none', fontSize: 13 }}
              slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
            />
          </Box>
        )}
      </SectionCard>

      {/* ── Add Dialog ─────────────────────────── */}
      <Dialog
        open={addDialogOpen}
        onClose={saveLoading ? undefined : () => setAddDialogOpen(false)}
        PaperProps={{ sx: { ...cardSx, minWidth: 360, p: 1 } }}
      >
        <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Add Barcode</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <InvTextField
                label="Barcode Value *"
                value={newBarcode.value}
                onChange={e => setNewBarcode(p => ({ ...p, value: e.target.value }))}
                inputProps={{ minLength: 4 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InvSelect
                label="Barcode Type"
                value={newBarcode.type}
                onChange={e => setNewBarcode(p => ({ ...p, type: e.target.value }))}
                options={BARCODE_TYPES}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* TODO: replace PACK_OPTIONS with uoms prop once backend supports barcode-level UOM */}
              <InvSelect
                label="Pack Size"
                value={newBarcode.uomId}
                onChange={e => setNewBarcode(p => ({ ...p, uomId: e.target.value }))}
                options={PACK_OPTIONS}
              />
            </Grid>
            {formError && <Grid item xs={12}><Alert severity="error">{formError}</Alert></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAddDialogOpen(false)} disabled={saveLoading} variant="outlined"
            sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={saveLoading} variant="contained"
            sx={{ backgroundColor: IPD_COLORS.primary }}
            startIcon={saveLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ──────────────── */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={deleteLoading ? undefined : () => setDeleteTarget(null)}
        PaperProps={{ sx: { ...cardSx, minWidth: 340, p: 1 } }}
      >
        <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Delete barcode?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: IPD_COLORS.textSecondary }}>
            Delete barcode <strong style={{ fontFamily: 'monospace' }}>{deleteTarget?.value}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading} variant="outlined"
            sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} disabled={deleteLoading} variant="contained"
            sx={{ backgroundColor: IPD_COLORS.error.text }}
            startIcon={deleteLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
